/**
 * Device metadata utilities for the web client.
 *
 * Generates and persists a stable device ID in localStorage, and collects
 * static platform metadata to be sent with login/register requests so the
 * server can associate tokens with the originating device.
 */

const DEVICE_ID_KEY = 'locs-device-id';
const CLIENT_VERSION = '0.1.0';

/**
 * Returns the stable device ID for this browser profile.
 * Generated once and persisted in localStorage; survives page refreshes.
 */
export function getWebDeviceId(): string {
  try {
    const stored = localStorage.getItem(DEVICE_ID_KEY);
    if (stored) return stored;
    const id = generateUUID();
    localStorage.setItem(DEVICE_ID_KEY, id);
    return id;
  } catch {
    // Fallback when localStorage is unavailable (private browsing edge cases).
    return generateUUID();
  }
}

/**
 * Returns the device metadata object to attach to auth requests.
 */
export function getWebDeviceMetadata() {
  return {
    deviceId: getWebDeviceId(),
    clientType: 'webapp',
    runtimePlatform: 'web',
    implementation: getBrowserImplementation(),
    clientVersion: CLIENT_VERSION,
  };
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function generateUUID(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  // Fallback for older browsers
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

function getBrowserImplementation(): string {
  try {
    const ua = navigator.userAgent;
    if (ua.includes('Firefox/')) return 'firefox';
    if (ua.includes('Edg/')) return 'edge';
    if (ua.includes('OPR/') || ua.includes('Opera/')) return 'opera';
    if (ua.includes('Chrome/')) return 'chrome';
    if (ua.includes('Safari/')) return 'safari';
    return 'browser';
  } catch {
    return 'browser';
  }
}
